import { Checkbox } from "@fluentui/react/lib/Checkbox";
import { mergeStyleSets } from "@uifabric/merge-styles";
import DatePickerField from "components/controls/DatePickerField";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "store/hooks";
import { useTheme } from "styled-components";
import { IStyledTheme } from "theme/types";

interface PremiumDomainData {
  Enabled: boolean;
  CoverageFrom?: Date;
  CoverageTo?: Date;
  Urgent?: boolean;
}

interface ClientCommentData {
  [domainCode: string]: PremiumDomainData;
}

interface Props {
  value?: string;
  onChange: (name: string, value: string) => void;
  name: string;
  disabled?: boolean;
  label?: string;
  isMarked?: boolean;
  errors?: any;
  route?: string;
}

const getClassNames = (theme: IStyledTheme) =>
  mergeStyleSets({
    container: {
      border: `1px solid ${theme.palette.neutralTertiaryAlt}`,
      borderRadius: "2px",
      padding: "16px",
      marginTop: "8px",
    },
    table: {
      display: "table",
      width: "100%",
      borderSpacing: "0 8px",
    },
    tableRow: {
      display: "table-row",
    },
    checkboxCell: {
      display: "table-cell",
      width: "25%",
      verticalAlign: "middle",
      paddingRight: "20px",
    },
    dateCell: {
      display: "table-cell",
      width: "25%",
      verticalAlign: "middle",
      paddingRight: "16px",
    },
    urgentCell: {
      display: "table-cell",
      width: "25%",
      verticalAlign: "middle",
      paddingRight: "16px",
    },
    label: {
      fontWeight: 600,
      fontSize: "14px",
      color: theme.palette.black,
      marginBottom: "8px",
    },
    isMarked: {
      background: theme.palette.yellowLight,
    },
  });

const ClientCommentExternalUser: React.FC<Props> = ({
  value,
  onChange,
  name,
  disabled = false,
  label,
  isMarked,
  errors,
  route,
}) => {
  const { t } = useTranslation();
  const theme = useTheme() as IStyledTheme;
  const classes = getClassNames(theme);

  // Get premium domains from vehicle app settings
  const vehicleAppSettings = useSelector((s) => s.vehicle.vehicleAppSettings);

  const [data, setData] = useState<ClientCommentData>({});

  // Access control selectors
  const vehicleStatusCode = useSelector(
    (s) => s.vehicle?.vehicle?.vehicleStatusCode
  );
  const taxonomy = useSelector((s) => s.taxonomy);
  const vehicleStatusTaxCode =
    taxonomy?.VehicleStatusCode.byId[vehicleStatusCode]?.code;
  const userRole = useSelector((s) => s.auth?.userRole);
  // Determine if component should be Enabled
  const isComponentEnabled = () => {
    // Always Enabled for new vehicles
    if (route === "createVehicle" || route === "duplicateVehicle") {
      return true;
    }

    // Check if vehicle status is PENDING_FOR_ACTIVATION
    // const isPendingForActivation =
    //   vehicleStatusTaxCode === "VehicleStatusCode.PENDING_FOR_ACTIVATION";

    // Check if user has appropriate role
    const hasAppropriateRole = userRole === "external" || userRole === "admin";

    //return isPendingForActivation && hasAppropriateRole;
    return hasAppropriateRole;
  };

  const componentDisabled = disabled || !isComponentEnabled();

  // Helper function to serialize Date object to YYYY-MM-DD string (no timezone conversion)
  const serializeDate = (date?: Date): string | undefined => {
    if (!date) return undefined;

    // Get local date components to avoid timezone conversion
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  // Helper function to parse date string to Date object (local timezone)
  const parseDate = (dateString?: string): Date | undefined => {
    if (!dateString) return undefined;
    try {
      // Parse as local date to avoid timezone issues
      // dateString format: "YYYY-MM-DD"
      const [year, month, day] = dateString.split("-").map(Number);
      if (year && month && day) {
        // Create date in local timezone (month is 0-indexed)
        return new Date(year, month - 1, day);
      }
      return undefined;
    } catch {
      return undefined;
    }
  };

  // Initialize data structure based on available premium domains
  useEffect(() => {
    const premiumDomains = vehicleAppSettings?.VEHICLE_PREMIUM_DOMAINS || [];
    if (premiumDomains.length === 0) return;

    const initialData: ClientCommentData = {};

    // Always initialize with default structure
    premiumDomains.forEach((domain) => {
      initialData[domain] = { Enabled: false, Urgent: false };
    });

    // Parse and merge existing value if available
    if (value) {
      try {
        const parsed = JSON.parse(value);
        premiumDomains.forEach((domain) => {
          if (parsed[domain]) {
            initialData[domain] = {
              Enabled: parsed[domain].Enabled || false,
              CoverageFrom: parseDate(parsed[domain].CoverageFrom),
              CoverageTo: parseDate(parsed[domain].CoverageTo),
              Urgent: parsed[domain].Urgent || false,
            };
          }
        });
      } catch (error) {
        console.warn("Failed to parse client comment value:", error);
        // Use initial data if parsing fails
      }
    }

    setData(initialData);
  }, [value, vehicleAppSettings]); // Properly depend on value changes

  // Update parent when data changes
  const updateParent = useCallback(
    (newData: ClientCommentData) => {
      // Serialize dates to strings for JSON storage
      const serializedData = Object.keys(newData).reduce((acc, domain) => {
        acc[domain] = {
          Enabled: newData[domain].Enabled,
          CoverageFrom: serializeDate(newData[domain].CoverageFrom),
          CoverageTo: serializeDate(newData[domain].CoverageTo),
          Urgent: newData[domain].Urgent,
        };
        return acc;
      }, {} as any);

      const jsonString = JSON.stringify(serializedData);
      onChange(name, jsonString);
    },
    [onChange, name]
  );

  const handleCheckboxChange = (domainCode: string, checked: boolean) => {
    if (componentDisabled) return;

    const newData = {
      ...data,
      [domainCode]: {
        ...data[domainCode],
        Enabled: checked,
        // Clear date fields and urgent when unchecking
        CoverageFrom: checked ? data[domainCode]?.CoverageFrom : undefined,
        CoverageTo: checked ? data[domainCode]?.CoverageTo : undefined,
        Urgent: checked ? data[domainCode]?.Urgent : false,
      },
    };
    setData(newData);
    updateParent(newData);
  };

  const handleUrgentChange = (domainCode: string, checked: boolean) => {
    if (componentDisabled || !data[domainCode]?.Enabled) return;

    const newData = {
      ...data,
      [domainCode]: {
        ...data[domainCode],
        Urgent: checked,
      },
    };
    setData(newData);
    updateParent(newData);
  };

  const handleDateChange = (
    domainCode: string,
    field: "CoverageFrom" | "CoverageTo",
    dateValue: Date
  ) => {
    if (componentDisabled) return;
    if (typeof dateValue === "undefined") return; // Ignore string inputs

    const newData = {
      ...data,
      [domainCode]: {
        ...data[domainCode],
        [field]: dateValue,
      },
    };
    setData(newData);
    updateParent(newData);
  };

  // Helper function to get domain label
  const getDomainLabel = (domain: any) => {
    const translated = t(domain);
    return translated;
  };

  if (!vehicleAppSettings?.VEHICLE_PREMIUM_DOMAINS.length) {
    return null; // Don't render if no premium domains available
  }

  return (
    <div className={isMarked ? classes.isMarked : ""}>
      {label && <div className={classes.label}>{t(label)}</div>}
      <div className={classes.container}>
        <div className={classes.table}>
          {vehicleAppSettings?.VEHICLE_PREMIUM_DOMAINS.map((domain) => (
            <div key={domain} className={classes.tableRow}>
              <div className={classes.checkboxCell}>
                <Checkbox
                  label={getDomainLabel(domain)}
                  checked={data[domain]?.Enabled || false}
                  onChange={(_, checked) =>
                    handleCheckboxChange(domain, !!checked)
                  }
                  disabled={componentDisabled}
                />
              </div>
              <div className={classes.dateCell}>
                <DatePickerField
                  name={`${name}_${domain}_from`}
                  label={t("bfm.CoverageFrom.label")}
                  defaultValue={
                    data[domain]?.Enabled
                      ? data[domain]?.CoverageFrom
                      : undefined
                  }
                  setFieldValue={(_, value) =>
                    handleDateChange(domain, "CoverageFrom", value)
                  }
                  onChange={() => {}}
                  errors={{}}
                  disabled={componentDisabled || !data[domain]?.Enabled}
                  placeholder={t("bfm.selectDate")}
                />
              </div>
              <div className={classes.dateCell}>
                <DatePickerField
                  name={`${name}_${domain}_to`}
                  label={t("bfm.CoverageTo.label")}
                  defaultValue={
                    data[domain]?.Enabled ? data[domain]?.CoverageTo : undefined
                  }
                  setFieldValue={(_, value) =>
                    handleDateChange(domain, "CoverageTo", value)
                  }
                  onChange={() => {}}
                  errors={{}}
                  disabled={componentDisabled || !data[domain]?.Enabled}
                  placeholder={t("bfm.selectDate")}
                />
              </div>
              <div className={classes.urgentCell}>
                <Checkbox
                  label={t("bfm.urgent.label")}
                  checked={data[domain]?.Urgent || false}
                  onChange={(_, checked) =>
                    handleUrgentChange(domain, !!checked)
                  }
                  disabled={componentDisabled || !data[domain]?.Enabled}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ClientCommentExternalUser;
